import {
  isUsedInspectionRework, complianceTabFingerprint,
  complianceAnswersFingerprint, inspectionReworkMeasurements,
  requiredInspectionReworkMeasurements
} from '@/configs/inspectionRework'
import { list as statuses } from '@/configs/statuses'

const initialState = () => ({
  id: null, stage: 'inspection', measurements: [], tabs: {}, pendingSave: null, pendingTransition: null,
  pendingAnswersSave: null
})

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    reset (state, id = null) {
      Object.assign(state, initialState(), { id })
    },
    setStage (state, stage) {
      state.stage = stage
    },
    confirmMeasurement (state, type) {
      if (!state.measurements.includes(type)) state.measurements.push(type)
      // A measurement edit invalidates the following stages.
      state.tabs = {}
      state.pendingAnswersSave = null
      state.stage = 'measurements'
    },
    invalidateMeasurement (state, type) {
      state.measurements = state.measurements.filter(item => item !== type)
      state.tabs = {}
      state.pendingAnswersSave = null
    },
    setTabs (state, tabs) {
      state.tabs = tabs
    },
    setPendingSave (state, payload) {
      state.pendingSave = payload
    },
    setPendingAnswersSave (state, signature) {
      state.pendingAnswersSave = signature
    },
    setPendingTransition (state, payload) {
      state.pendingTransition = payload
    },
    invalidateMeasurements (state) {
      state.stage = 'inspection'
      state.measurements = []
      state.tabs = {}
      state.pendingAnswersSave = null
    },
    startMeasurements (state) {
      state.stage = 'measurements'
      state.measurements = []
      state.tabs = {}
      state.pendingSave = null
      state.pendingAnswersSave = null
    }
  },
  actions: {
    sync ({ state, rootState, commit }, inspection) {
      // Keep the finishing UI until regeneration and refresh are both confirmed.
      if (state.pendingTransition && String(state.id) === String(inspection.id)) return
      if (!isUsedInspectionRework(rootState.statement.statement, inspection)) {
        if (state.id) commit('reset')
      } else if (String(state.id) !== String(inspection.id)) {
        commit('reset', inspection.id)
      }
      if (isUsedInspectionRework(rootState.statement.statement, inspection)) {
        if (inspection.status_document === statuses.inspectionUploadProofs) commit('setStage', 'proofs')
        else if (state.stage === 'proofs') commit('reset', inspection.id)
      }
    },
    async refreshMeasurements ({ rootState, commit, dispatch }, id) {
      if (String(rootState.inspection.inspection.id) !== String(id)) return false
      const inspection = rootState.inspection.inspection
      const results = await Promise.allSettled(Object.entries(inspectionReworkMeasurements)
        .map(async ([type, descriptor]) => {
          if (inspection[descriptor.flag]) return dispatch(descriptor.load, id, { root: true })
          // A deleted measurement must not survive in the client store.
          const data = ['lightTransmission', 'gas', 'smoke'].includes(type) ? [] : {}
          commit('SET_DATA', { store: 'inspection', state: descriptor.state, data }, { root: true })
          return true
        }))
      return results.every(result => result.status === 'fulfilled' && result.value === true)
    },
    async saveInspection ({ state, rootState, commit, dispatch }, payload) {
      if (state.pendingTransition) return { isResult: false }
      if (!isUsedInspectionRework(rootState.statement.statement, rootState.inspection.inspection) ||
        String(payload.id) !== String(rootState.inspection.inspection.id)) return { isResult: false }
      try {
        const signature = JSON.stringify(payload)
        if (state.pendingSave !== signature) {
          commit('invalidateMeasurements')
          const result = await dispatch('updateInspectionFromFinalization', payload, { root: true })
          if (!result.isResult) return { isResult: false }
          commit('setPendingSave', signature)
        }
        if (!await dispatch('getInspection', payload.id, { root: true })) return { isResult: false }
        if (!isUsedInspectionRework(rootState.statement.statement, rootState.inspection.inspection)) {
          return { isResult: false }
        }
        if (!await dispatch('refreshMeasurements', payload.id)) return { isResult: false }
        const hasMeasurements = requiredInspectionReworkMeasurements(
          rootState.inspection.inspection, rootState.statement.statement).length > 0
        if (!hasMeasurements) {
          if (!await dispatch('getInspectionQuestions', payload.id, { root: true })) return { isResult: false }
          commit('SET_DATA', { store: 'inspection', state: 'errorDownloadCompliance', data: false }, { root: true })
        }
        commit('startMeasurements')
        if (!hasMeasurements) commit('setStage', 'compliance')
        return { isResult: true, id: payload.id }
      } catch (error) {
        return { isResult: false }
      }
    },
    async saveAnswers ({ state, rootState, commit, dispatch }, { id, body }) {
      if (state.pendingTransition || state.stage !== 'compliance' ||
        String(id) !== String(state.id) || !Array.isArray(body) || !body.length ||
        rootState.inspection.inspection.status_document !== statuses.inspectionWaitComplianceCompleted ||
        !isUsedInspectionRework(rootState.statement.statement, rootState.inspection.inspection)) return false

      const fingerprint = complianceAnswersFingerprint(body)
      const answersUnchanged = () => {
        const savedById = new Map(rootState.inspection.questions
          .flatMap(tab => tab.inspection_questions).map(question => [question.id, question]))
        const saved = body.map(question => savedById.get(question.id))
        return saved.every(Boolean) && complianceAnswersFingerprint(saved) === fingerprint
      }
      try {
        if (!await dispatch('getInspectionQuestions', id, { root: true })) return false
        // Unchanged answers need no write. An acknowledged PATCH is not repeated if a later read failed.
        if (state.pendingAnswersSave !== fingerprint && !answersUnchanged()) {
          if (!await dispatch('setInspectionAnswers', { inspectionID: id, body }, { root: true })) return false
          commit('setPendingAnswersSave', fingerprint)
          if (!await dispatch('getInspectionQuestions', id, { root: true })) return false
        }
        if (!await dispatch('getInspection', id, { root: true })) return false
        commit('setPendingAnswersSave', null)
        return true
      } catch (error) {
        return false
      }
    },
    async transition ({ state, rootState, commit, dispatch }, to) {
      const id = state.id
      const statementId = rootState.statement.statement.id
      if (!id || (!state.pendingTransition &&
        !isUsedInspectionRework(rootState.statement.statement, rootState.inspection.inspection))) return false
      if (!['proofs', 'signatures'].includes(to) ||
        (state.pendingTransition && state.pendingTransition !== to)) return false
      if (!state.pendingTransition) {
        if (to === 'signatures' &&
          (state.stage !== 'proofs' ||
            rootState.inspection.inspection.status_document !== statuses.inspectionUploadProofs)) return false
        if (to === 'proofs' && (state.stage !== 'compliance' || !rootState.inspection.questions.length ||
          rootState.inspection.inspection.status_document !== statuses.inspectionWaitComplianceCompleted ||
          !rootState.inspection.questions.every(tab => state.tabs[tab.id] === complianceTabFingerprint(tab)))) return false
      }
      try {
        if (to === 'proofs') {
          const status = statuses.inspectionUploadProofs
          if (!state.pendingTransition) {
            if (!await dispatch('getInspectionQuestions', id, { root: true })) return false
            if (!rootState.inspection.questions.length || !rootState.inspection.questions
              .every(tab => state.tabs[tab.id] === complianceTabFingerprint(tab))) return false
            if (!await dispatch('changeInspectionReworkStatus', { id, status }, { root: true })) return false
            commit('setPendingTransition', to)
          }
          if (!await dispatch('getInspection', id, { root: true }) ||
            rootState.inspection.inspection.status_document !== status) return false
          if (!await dispatch('getStatement', statementId, { root: true }) ||
            !isUsedInspectionRework(rootState.statement.statement, rootState.inspection.inspection)) return false
          if (!await dispatch('getProofs', id, { root: true })) return false
          commit('setStage', to)
          commit('setPendingTransition', null)
          return true
        }
        if (!state.pendingTransition) {
          if (!await dispatch('sendInspectionForSignature', id, { root: true })) return false
          commit('setPendingTransition', to)
        }
        // If a refresh fails, retry only the reads/generation, not the acknowledged transition.
        if (!await dispatch('refreshInspectionSubmission', { id, statementId, regenerateProtocol: true },
          { root: true })) return false
        commit('reset')
        return true
      } catch (error) {
        return false
      }
    }
  }
}
